import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useAppBusy } from '../utils/appUpdate';
import '../styles/WebcamCaptureModal.css';

/**
 * In-page webcam capture modal. Used on desktop where the native file-input
 * `capture` attribute is ignored. Streams getUserMedia into a <video>, lets
 * the user snap a frame, preview, retake, and confirm. Returns a JPEG File
 * via onCapture(file).
 */
function WebcamCaptureModal({ open, onClose, onCapture }) {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const [error, setError] = useState('');
  const [snapshotUrl, setSnapshotUrl] = useState(null);
  const [starting, setStarting] = useState(false);
  useAppBusy('webcam-capture', Boolean(open));

  const stopStream = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
  }, []);

  const startStream = useCallback(async () => {
    setError('');
    setStarting(true);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'user', width: { ideal: 1280 }, height: { ideal: 960 } },
        audio: false,
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play().catch(() => {});
      }
    } catch (err) {
      console.error('getUserMedia failed:', err);
      if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
        setError('Camera access was denied. Allow camera permission in your browser settings and try again.');
      } else if (err.name === 'NotFoundError' || err.name === 'DevicesNotFoundError') {
        setError('No camera was detected on this device.');
      } else {
        setError(`Could not start camera: ${err.message || err.name || 'unknown error'}`);
      }
    } finally {
      setStarting(false);
    }
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    setSnapshotUrl(null);
    startStream();
    return () => {
      stopStream();
      setSnapshotUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return null;
      });
    };
  }, [open, startStream, stopStream]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose?.();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  const takeSnapshot = () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return;
    const w = video.videoWidth;
    const h = video.videoHeight;
    if (!w || !h) {
      setError('Camera is still warming up — try again in a moment.');
      return;
    }
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(video, 0, 0, w, h);
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          setError('Failed to capture image. Try again.');
          return;
        }
        if (snapshotUrl) URL.revokeObjectURL(snapshotUrl);
        setSnapshotUrl(URL.createObjectURL(blob));
        stopStream();
      },
      'image/jpeg',
      0.92
    );
  };

  const retake = () => {
    if (snapshotUrl) URL.revokeObjectURL(snapshotUrl);
    setSnapshotUrl(null);
    startStream();
  };

  const useSnapshot = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          setError('Could not finalize photo. Try again.');
          return;
        }
        const file = new File([blob], `webcam-${Date.now()}.jpg`, { type: 'image/jpeg' });
        if (snapshotUrl) URL.revokeObjectURL(snapshotUrl);
        setSnapshotUrl(null);
        onCapture?.(file);
        onClose?.();
      },
      'image/jpeg',
      0.92
    );
  };

  return (
    <div className="webcam-modal-overlay" role="dialog" aria-modal="true" onClick={onClose}>
      <div className="webcam-modal" onClick={(e) => e.stopPropagation()}>
        <div className="webcam-modal-header">
          <h3 className="webcam-modal-title">Capture Photo</h3>
          <button type="button" className="webcam-modal-close" onClick={onClose} aria-label="Close">
            ✕
          </button>
        </div>

        <div className="webcam-modal-body">
          {error ? (
            <div className="webcam-error">
              <p>{error}</p>
              <button type="button" className="btn btn-primary" onClick={startStream} disabled={starting}>
                {starting ? 'Retrying…' : 'Try again'}
              </button>
            </div>
          ) : snapshotUrl ? (
            <div className="webcam-preview">
              <img src={snapshotUrl} alt="Captured preview" />
            </div>
          ) : (
            <div className="webcam-preview">
              <video ref={videoRef} playsInline muted autoPlay />
              {starting && <div className="webcam-status">Starting camera…</div>}
            </div>
          )}
          <canvas ref={canvasRef} style={{ display: 'none' }} />
        </div>

        <div className="webcam-modal-actions">
          {!error && !snapshotUrl && (
            <>
              <button type="button" className="btn btn-outline" onClick={onClose}>
                Cancel
              </button>
              <button type="button" className="btn btn-primary" onClick={takeSnapshot} disabled={starting}>
                📸 Take Photo
              </button>
            </>
          )}
          {!error && snapshotUrl && (
            <>
              <button type="button" className="btn btn-outline" onClick={retake}>
                Retake
              </button>
              <button type="button" className="btn btn-primary" onClick={useSnapshot}>
                Use This Photo
              </button>
            </>
          )}
          {error && (
            <button type="button" className="btn btn-outline" onClick={onClose}>
              Cancel
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default WebcamCaptureModal;
